/**
 * constants/alarmTone.ts
 * Alarm tone display metadata for the clock alarm settings UI.
 *
 * Provides human-friendly display names and an ordered ID list for
 * the 5 built-in alarm tones. Audio synthesis parameters live in
 * `utils/alarmSound.ts` (implementation detail, not display data).
 *
 * Corresponds to the `AlarmToneId` type in `types/shared/AlarmRule.ts`.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 */

import type { AlarmToneId } from '@/types/shared/AlarmRule';

/**
 * Ordered array of all built-in alarm tone IDs.
 * Used for iteration in settings UI dropdowns and selectors.
 */
export const ALARM_TONE_IDS = [
  'chime',
  'bell',
  'alert',
  'gentle-ping',
  'urgent',
] as const satisfies readonly AlarmToneId[];

/**
 * Human-friendly display names for each alarm tone.
 * Keyed by `AlarmToneId` for direct lookup in UI components.
 */
export const ALARM_TONE_DISPLAY_MAP = {
  'chime': 'Chime',
  'bell': 'Bell',
  'alert': 'Alert',
  'gentle-ping': 'Gentle Ping',
  'urgent': 'Urgent',
} as const satisfies Readonly<Record<AlarmToneId, string>>;

/**
 * Compile-time exhaustiveness check ensuring all AlarmToneId values are covered.
 *
 * Reduces to `true` when every AlarmToneId union member exists as a key in
 * ALARM_TONE_DISPLAY_MAP. Adding a new AlarmToneId without updating the map
 * will cause a compilation error.
 */
type _AllAlarmTonesCovered =
  Exclude<AlarmToneId, keyof typeof ALARM_TONE_DISPLAY_MAP> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage check — bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _AlarmToneChecks = _AllAlarmTonesCovered;
